// QuadraticCountAndSolveTask.js
import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import SelectInput from '../../../../components/inputs/SelectInput';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';
import { toTextFractionLatex } from '../../../../utils/fractionLatex';

/* ===== helpers: format stron (ukrywamy współczynnik 1 przy x^2 i x) ===== */
function termLatex(coef, variable, isFirst) {
  if (coef === 0) return '';
  const sign = coef < 0 ? '-' : '+';
  const abs = Math.abs(coef);
  const body = variable ? (abs === 1 ? `${variable}` : `${abs}${variable}`) : `${abs}`;
  return isFirst ? (coef < 0 ? `-${body}` : `${body}`) : ` ${sign} ${body}`;
}
function sideFromCoeffs({ x2 = 0, x = 0, c = 0 }) {
  const parts = [];
  const push = (coef, variable) => {
    const isFirst = parts.length === 0;
    const t = termLatex(coef, variable, isFirst);
    if (t) parts.push(t);
  };
  push(x2, 'x^2');
  push(x, 'x');
  push(c, '');
  return parts.length ? parts.join('') : '0';
}

const labels = { '0': 'brak rozwiązań', '1': 'jedno rozwiązanie', '2': 'dwa rozwiązania' };
const within = (x, y) => Math.abs(x - y) < 0.01;

/* ===== format liczb do LaTeX ===== */
// trwalszy gcd dla dowolnych znaków
function igcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b !== 0) {
    const r = a % b;
    a = b; b = r;
  }
  return a;
}
function gcd3(a, b, c) { return igcd(igcd(a, b), c); }

function formatFractionLatex(value) {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 20; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = igcd(n, d);
      const num = n / g, den = d / g;
      return intPart === 0 ? `${sign}\\tfrac{${num}}{${den}}` : `${sign}${intPart}\\tfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

/* ===== generowanie: 1:1:1 dla Δ<0, Δ=0, Δ>0 ===== */
export function generateTask() {
  const target = ['0', '1', '2'][Math.floor(Math.random() * 3)];
  let a, b, c, delta, x1, x2;

  if (target === '1') {
    // Δ = 0: b = -2 a m, c = a m^2 (pierwiastek podwójny m)
    a = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 3) + 1); // ±1..±3
    const m = (Math.floor(Math.random() * 7) + 1) * (Math.random() < 0.5 ? -1 : 1); // ±1..±7
    b = -2 * a * m;
    c = a * m * m;
    delta = 0;
    x1 = x2 = m;
  } else if (target === '2') {
    // Δ > 0: konstruujemy z dwóch różnych pierwiastków m != n
    a = (Math.random() < 0.5 ? -1 : 1) * (Math.floor(Math.random() * 3) + 1);
    let m = (Math.floor(Math.random() * 6) + 1) * (Math.random() < 0.5 ? -1 : 1);
    let n;
    do { n = (Math.floor(Math.random() * 6) + 1) * (Math.random() < 0.5 ? -1 : 1); } while (n === m);
    b = -a * (m + n);
    c = a * m * n;
    delta = b * b - 4 * a * c; // > 0
    x1 = m; x2 = n;
  } else {
    // Δ < 0: dobieramy a,c o tym samym znaku i |b| tak, by b^2 < 4ac
    a = (Math.random() < 0.5 ? 1 : -1) * (Math.floor(Math.random() * 3) + 1);
    const cAbs = Math.floor(Math.random() * 9) + 2; // 2..10
    c = cAbs * Math.sign(a);
    let bCand;
    while (true) {
      bCand = (Math.floor(Math.random() * 7) + 1) * (Math.random() < 0.5 ? -1 : 1);
      if (bCand * bCand < 4 * a * c) break;
    }
    b = bCand;
    delta = b * b - 4 * a * c; // < 0
    x1 = x2 = null;
  }

  // budujemy „porozrzucane” równanie (niekoniecznie = 0)
  let moveA = Math.random() < 0.5, moveB = Math.random() < 0.5, moveC = Math.random() < 0.5;
  if (!moveA && !moveB && !moveC) moveA = true;
  if (moveA && moveB && moveC) moveC = false;
  const leftCoeffs  = { x2: moveA ? 0 : a, x: moveB ? 0 : b, c: moveC ? 0 : c };
  const rightCoeffs = { x2: moveA ? -a : 0, x: moveB ? -b : 0, c: moveC ? -c : 0 };

  const originalLatex = `${sideFromCoeffs(leftCoeffs)} = ${sideFromCoeffs(rightCoeffs)}`;
  const standardLatex = `${sideFromCoeffs({ x2: a, x: b, c })} = 0`;
  const numSolutions = delta > 0 ? '2' : delta === 0 ? '1' : '0';

  return {
    type: 'QuadraticCountAndSolveTask',
    data: { a, b, c, delta, numSolutions, originalLatex, standardLatex, x1, x2 }
  };
}

/* ===== treść ===== */
export function renderTask({ data }) {
  return (
    <p>
      Określ liczbę rozwiązań i — jeśli istnieją — podaj je: <InlineMath math={data.originalLatex} />
    </p>
  );
}

/* ===== input ===== */
export function renderInput(_, userInput = {}, onChange, disabled) {
  const count = userInput.count ?? '';
  const firstLabel = count === '1' ? 'x_0 =' : 'x_1 =';

  return (
    <div className='flex flex-col gap-2'>
      <SelectInput
        options={[
          { value: '0', label: 'brak rozwiązań' },
          { value: '1', label: 'jedno rozwiązanie' },
          { value: '2', label: 'dwa rozwiązania' }
        ]}
        value={count}
        onChange={(e) => onChange({ ...userInput, count: e?.target?.value ?? e })}
        placeholder='Wybierz liczbę rozwiązań'
        disabled={disabled}
      />
      {(count === '1' || count === '2') && (
        <div className='flex items-center gap-2'>
          <span><InlineMath math={firstLabel} /></span>
          <FractionInput
            value={userInput.x1 || ''}
            onChange={(val) => onChange({ ...userInput, x1: val })}
            disabled={disabled}
            label={count === '1' ? 'x₀' : 'x₁'}
          />
        </div>
      )}
      {count === '2' && (
        <div className='flex items-center gap-2'>
          <span><InlineMath math={'x_2 ='} /></span>
          <FractionInput
            value={userInput.x2 || ''}
            onChange={(val) => onChange({ ...userInput, x2: val })}
            disabled={disabled}
            label='x₂'
          />
        </div>
      )}
    </div>
  );
}

/* ===== sprawdzanie ===== */
export function checkAnswer({ data }, userInput = {}) {
  const picked = userInput.count;
  const { numSolutions, x1, x2 } = data;

  if (picked !== numSolutions) {
    return { correct: false, expected: [numSolutions, x1, x2] };
  }

  if (numSolutions === '0') return { correct: true, expected: [numSolutions] };

  const u1 = userInput.x1?.parsed;
  if (u1 == null || isNaN(u1)) return { correct: false, expected: [numSolutions, x1, x2] };

  if (numSolutions === '1') {
    const ok = within(u1, x1);
    return { correct: ok, expected: [numSolutions, x1] };
  }

  const u2 = userInput.x2?.parsed;
  if (u2 == null || isNaN(u2)) return { correct: false, expected: [numSolutions, x1, x2] };

  const ok =
    (within(u1, x1) && within(u2, x2)) ||
    (within(u1, x2) && within(u2, x1));

  return { correct: ok, expected: [numSolutions, x1, x2] };
}

/* ===== poprawna odpowiedź (krótka) ===== */
export function renderAnswer({ data }) {
  const { numSolutions, x1, x2 } = data;
  return (
    <div style={{ textAlign: 'left', marginTop: '0.5rem' }}>
      <div><strong>Liczba rozwiązań:</strong> {labels[numSolutions]}</div>

      {numSolutions === '1' && (
        <div style={{ marginTop: '0.5rem' }}>
          <InlineMath math={`x_0 = ${toTextFractionLatex(formatFractionLatex(x1))}`} />
        </div>
      )}

      {numSolutions === '2' && (
        <>
          <div style={{ marginTop: '0.5rem' }}>
            <InlineMath math={`x_1 = ${toTextFractionLatex(formatFractionLatex(x1))}`} />
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <InlineMath math={`x_2 = ${toTextFractionLatex(formatFractionLatex(x2))}`} />
          </div>
        </>
      )}
    </div>
  );
}

/* ===== wyjaśnienie (z krokiem upraszczania, jeśli gcd>1) ===== */
export function renderExplanation({ data }) {
  const { a, b, c, numSolutions, originalLatex, standardLatex, x1, x2 } = data;

  // ewentualne uproszczenie: dzielimy przez gcd(a,b,c)
  const g = gcd3(a, b, c);
  const simplified = g > 1;
  const aR = simplified ? a / g : a;
  const bR = simplified ? b / g : b;
  const cR = simplified ? c / g : c;
  const standardReducedLatex = `${sideFromCoeffs({ x2: aR, x: bR, c: cR })} = 0`;

  // podstawienia liczymy już na zredukowanych współczynnikach
  const deltaR = bR * bR - 4 * aR * cR;
  const denom = 2 * aR;

  // -b w LaTeX: bez nawiasu gdy dodatnie (np. bR = -12 -> "-b" = 12; bR = 5 -> "-b" = -5)
  const negBLatex = bR < 0 ? `${-bR}` : `-${bR}`;
  const bPow2Latex = bR < 0 ? `(${bR})^2` : `${bR}^2`;
  const aFactor = aR < 0 ? `(${aR})` : `${aR}`;
  const cFactor = cR < 0 ? `(${cR})` : `${cR}`;

  return (
    <div className='space-y-4'>
      <p><strong>Krok 1:</strong> Przenosimy wszystko na jedną stronę i redukujemy:</p>
      <BlockMath math={`${originalLatex} \\Rightarrow ${standardLatex}`} />

      {simplified && (
        <>
          <p><strong>Krok 2:</strong> Możemy skrócić całe równanie przez <InlineMath math={`${g}`} /> (dla łatwiejszych obliczeń):</p>
          <BlockMath math={`${standardLatex} \\;\\Rightarrow\\; ${standardReducedLatex}`} />
        </>
      )}

      <p><strong>{simplified ? 'Krok 3' : 'Krok 2'}:</strong> Współczynniki i wyróżnik kwadratowy (delta):</p>
      <BlockMath math={`a = ${aR},\\quad b = ${bR},\\quad c = ${cR}`} />
      <p>Delta <InlineMath math={'\\Delta'} /> to <em>wyróżnik kwadratowy</em> równania:</p>
      <BlockMath math={'\\Delta = b^2 - 4ac'} />

      <p><strong>{simplified ? 'Krok 3 (cd.)' : 'Krok 2 (cd.)'}:</strong> Liczymy deltę:</p>
      <BlockMath math={`\\Delta = ${bPow2Latex} - 4 \\cdot ${aFactor} \\cdot ${cFactor} = ${deltaR}`} />

      {numSolutions === '0' && (
        <>
          <p><strong>{simplified ? 'Krok 4' : 'Krok 3'}:</strong> Ponieważ <InlineMath math={'\\Delta < 0'} /> — brak rozwiązań.</p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\boxed{\\text{brak rozwiązań}}'} />
          </div>
        </>
      )}

      {numSolutions === '1' && (
        <>
          <p><strong>{simplified ? 'Krok 4' : 'Krok 3'}:</strong> Ponieważ <InlineMath math={'\\Delta = 0'} /> — jedno rozwiązanie:</p>
          <BlockMath math={`\\begin{aligned}
x_0 &= \\frac{-b}{2a} \\\\[6pt]
x_0 &= \\frac{${negBLatex}}{2\\cdot${aR}} \\\\[6pt]
x_0 &= \\frac{${-bR}}{${denom}} \\\\[6pt]
x_0 &= ${formatFractionLatex(x1)}
\\end{aligned}`} />
          <p><strong>Odpowiedź końcowa:</strong></p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={`x_0 = ${formatFractionLatex(x1)}`} />
          </div>
        </>
      )}

      {numSolutions === '2' && (
        <>
          <p><strong>{simplified ? 'Krok 4' : 'Krok 3'}:</strong> Ponieważ <InlineMath math={'\\Delta > 0'} /> — dwa rozwiązania:</p>
          <BlockMath math={`\\begin{aligned}
x_1 &= \\frac{-b + \\sqrt{\\Delta}}{2a} \\\\[6pt]
x_1 &= \\frac{${negBLatex} + \\sqrt{${deltaR}}}{2\\cdot${aR}} \\\\[6pt]
x_1 &= \\frac{${-bR} + \\sqrt{${deltaR}}}{${denom}} \\\\[6pt]
x_1 &= ${formatFractionLatex(x1)}
\\end{aligned}`} />
          <BlockMath math={`\\begin{aligned}
x_2 &= \\frac{-b - \\sqrt{\\Delta}}{2a} \\\\[6pt]
x_2 &= \\frac{${negBLatex} - \\sqrt{${deltaR}}}{2\\cdot${aR}} \\\\[6pt]
x_2 &= \\frac{${-bR} - \\sqrt{${deltaR}}}{${denom}} \\\\[6pt]
x_2 &= ${formatFractionLatex(x2)}
\\end{aligned}`} />
          <p><strong>Odpowiedź końcowa:</strong></p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={`x_1 = ${formatFractionLatex(x1)},\\quad x_2 = ${formatFractionLatex(x2)}`} />
          </div>
        </>
      )}
    </div>
  );
}

/* ===== Twoja odpowiedź ===== */
export function renderUserAnswer(userInput = {}) {
  const count = userInput.count;
  if (!count) return <i>(brak odpowiedzi)</i>;

  const parts = [<div key='c'><strong>Liczba rozwiązań:</strong> {labels[count]}</div>];

  if (count === '1' || count === '2') {
    const symbol = count === '1' ? 'x_0' : 'x_1';
    const x1 = userInput.x1?.raw ? formatLatexPreview(userInput.x1.raw, symbol) : null;
    parts.push(
      <div key='x1' style={{ marginTop: '0.5rem' }}>
        {x1 ? <InlineMath math={x1} /> : <i>(brak {symbol})</i>}
      </div>
    );
  }
  if (count === '2') {
    const x2 = userInput.x2?.raw ? formatLatexPreview(userInput.x2.raw, 'x_2') : null;
    parts.push(
      <div key='x2' style={{ marginTop: '0.5rem' }}>
        {x2 ? <InlineMath math={x2} /> : <i>(brak x₂)</i>}
      </div>
    );
  }

  return <div style={{ textAlign: 'left', marginTop: '0.5rem' }}>{parts}</div>;
}
